import { useCallback, useEffect, useRef, useState } from 'react';
/**
 * Returns `value` after it has stayed unchanged for `delay` ms.
 */
export function useDebounce(value, delay) {
    var _a = useState(value), debouncedValue = _a[0], setDebouncedValue = _a[1];
    useEffect(function () {
        var id = setTimeout(function () { return setDebouncedValue(value); }, delay);
        return function () { return clearTimeout(id); };
    }, [value, delay]);
    return debouncedValue;
}
/**
 * Returns a function that runs `callback` after `delay` ms since the last call.
 * Any pending call is cancelled when `cancelKey` changes (e.g. URL-driven state).
 */
export function useDebouncedCallback(callback, delay, cancelKey) {
    var callbackRef = useRef(callback);
    callbackRef.current = callback;
    var timeoutRef = useRef(null);
    useEffect(function () {
        return function () {
            if (timeoutRef.current !== null) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);
    useEffect(function () {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, [cancelKey]);
    return useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(function () {
            timeoutRef.current = null;
            callbackRef.current.apply(callbackRef, args);
        }, delay);
    }, [delay]);
}
